import React, { useCallback } from 'react';
import './Home.css';
import logo from './ap-logo-4.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faPlus,
  faEdit,
  faTemperatureLow,
  faShoppingBasket,
  faTruck,
  faStar,
  faBuilding,
  faExclamationTriangle,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { useGlobal } from './GlobalContext';

const Home = () => {
  const navigate = useNavigate();
  const { missingFields, profileLoaded } = useGlobal();

  const handleSignOut = useCallback(() => {
    const isConfirmed = window.confirm("Are you sure you want to sign out?");
    if (isConfirmed) {
      signOut(getAuth())
        .catch(err => console.error('Error signing out', err))
        .finally(() => navigate('/', { replace: true }));
    }
  }, [navigate]);

  const navigateTo = useCallback((path) => navigate(path), [navigate]);

  // Group missing fields by tab for a cleaner display
  const missingByTab = missingFields.reduce((acc, f) => {
    if (!acc[f.tab]) acc[f.tab] = [];
    acc[f.tab].push(f.label);
    return acc;
  }, {});

  const hasMissing = missingFields.length > 0;

  return (
    <div className="home-page dashboard-container">
      <header className="top-nav">
        <div className="logo-container">
          <img src={logo} alt="Akshaya Patra Logo" className="logo" />
        </div>
        <h1 className="title">Client Portal</h1>
        <button
          className="signout-button"
          onClick={handleSignOut}
          aria-label="Sign out"
        >
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Sign Out</span>
        </button>
      </header>

      <main className="dashboard-content">

        {/* ── Missing fields banner ── */}
        {profileLoaded && hasMissing && (
          <div className="missing-banner">
            <div className="missing-banner-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="missing-banner-body">
              <p className="missing-banner-title">
                Your profile is incomplete — {missingFields.length} field{missingFields.length > 1 ? 's' : ''} missing
              </p>
              <p className="missing-banner-sub">
                You must complete your business information before placing orders.
              </p>
              <div className="missing-tabs">
                {Object.entries(missingByTab).map(([tab, fields]) => (
                  <div key={tab} className="missing-tab-group">
                    <span className="missing-tab-name">{tab}</span>
                    <ul className="missing-field-list">
                      {fields.map(f => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <button
              className="missing-banner-btn"
              onClick={() => navigateTo('/business-info')}
            >
              Complete profile <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}

        <div className="welcome-banner">
          <h2>Welcome back</h2>
          <p>Manage your orders and account details below.</p>
        </div>

        <section className="dashboard-grid" aria-label="Dashboard Options">
          <button
            className="dashboard-item"
            onClick={() => navigateTo('/business-info')}
            aria-label="Update Business Information"
          >
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faBuilding} className="dashboard-icon" />
            </div>
            <h3>Business Info</h3>
            <p>Update your mandatory account details</p>
            {hasMissing && (
              <span className="card-badge-warn">{missingFields.length} missing</span>
            )}
          </button>

          <button
            className="dashboard-item"
            onClick={() => navigateTo('/add-order')}
            aria-label="Create a new order"
          >
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faPlus} className="dashboard-icon" />
            </div>
            <h3>Add Order</h3>
            <p>Create new meal orders for your venue</p>
          </button>

          <button
            className="dashboard-item"
            onClick={() => navigateTo('/edit-order')}
            aria-label="View and edit existing orders"
          >
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faEdit} className="dashboard-icon" />
            </div>
            <h3>Manage Orders</h3>
            <p>View history and modify upcoming orders</p>
          </button>

          <button
            className="dashboard-item"
            onClick={() => navigateTo('/TempTracker')}
            aria-label="Track temperature logs"
          >
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faTemperatureLow} className="dashboard-icon" />
            </div>
            <h3>Temperature Log</h3>
            <p>Record and monitor food temperatures</p>
          </button>

          <button
            className="dashboard-item"
            onClick={() => navigateTo('/orders')}
            aria-label="View orders and estimation"
          >
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faShoppingBasket} className="dashboard-icon" />
            </div>
            <h3>Estimations</h3>
            <p>Check your order estimations and summaries</p>
          </button>

          <div className="dashboard-item under-development" aria-disabled="true">
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faTruck} className="dashboard-icon" />
            </div>
            <h3>Order Tracking</h3>
            <p>Live delivery updates (Coming Soon)</p>
          </div>

          <div className="dashboard-item under-development" aria-disabled="true">
            <div className="icon-wrapper">
              <FontAwesomeIcon icon={faStar} className="dashboard-icon" />
            </div>
            <h3>Reviews</h3>
            <p>Feedback and complaints (Coming Soon)</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
